import React, { useEffect, memo, useState, ChangeEvent, useContext } from 'react'
import { useSelector } from 'react-redux'
import { TextArea } from 'transfix-ui/components/TextArea'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { InlineRow } from 'apps/fleet-planner/components/load-details/InlineRow'
import { ExternalInstructionsDisplay } from 'apps/fleet-planner/components/load-details/stops/ExternalInstructionsDisplay'
import { ILoad } from 'apps/fleet-planner/types'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { DocumentIcon } from 'transfix-icons'
import { Button, InlineEditContainer, Card } from 'transfix-ui'
import DISPLAY from 'constants/display'
import { ExternalComplianceRequirements } from './ExternalComplianceRequirements'

export const NotesHeader = () => {
  return (
    <div className='flex flex-row items-center mb4'>
      <DocumentIcon size='large' color='coolGray5' />
      <span className='body-medium pl2 pt1'>Notes</span>
    </div>
  )
}

interface INotesDisplayProps {
  notes: ILoad['notes']
}

export const NotesDisplay = memo<INotesDisplayProps>(({ notes }) => (
  <span className='details-load-notes' data-test='details-load-notes'>
    {notes || DISPLAY.empty}
  </span>
))

interface INotesProps {
  shipperInsuranceRequirement?: string[]
  notesText?: string
  currentEditField?: string
  onEditModeRequested(name?: string): void
  updateLoad(changes: Partial<ILoad>): void
  isExternal?: boolean
  instructionsFromCustomer?: string
}

const NAME = 'notes'

export const LoadNotesInlineEdit = memo<INotesProps>(
  ({
    notesText = '',
    currentEditField,
    onEditModeRequested,
    updateLoad,
    isExternal,
    instructionsFromCustomer = '',
    shipperInsuranceRequirement = [],
  }) => {
    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
    const [notes, setNotes] = useState('')
    const isEditMode = currentEditField === NAME
    useEffect(() => {
      setNotes(notesText || '')
    }, [notesText])
    const closeHandler = () => {
      onEditModeRequested('')
    }
    const saveHandler = () => {
      updateLoad({ notes })
      closeHandler()
    }
    const onChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(event.target.value)
    }

    const { loading: fpLoading } = useSelector(loadDetailsSelector)
    const { isLoading: mpLoading } = useMarketplaceContext()

    return (
      <Card className='mt4 mb4'>
        <NotesHeader />
        <InlineRow
          rowTitle='Internal Notes'
          rowBody={
            <InlineEditContainer
              isLoading={fpLoading}
              name={NAME}
              editModeEnabled={isEditMode}
              dataTest='load-details-notes-section'
              editMode={
                <Card className='pa4'>
                  <div className='row'>
                    <TextArea
                      label='Notes'
                      placeholder='Write something…'
                      onChange={onChangeHandler}
                      dataTest='inline-edit-load-notes'
                      size='large'
                      value={notes}
                    />
                  </div>
                  <div className='row mt4 tr'>
                    <Button
                      color='tertiary'
                      size='large'
                      onClick={closeHandler}
                      data-test='load-notes-cancel'>
                      Cancel
                    </Button>
                    <Button
                      color='primary'
                      size='large'
                      onClick={saveHandler}
                      data-test='load-notes-submit'>
                      Save
                    </Button>
                  </div>
                </Card>
              }
              onEditModeRequested={onEditModeRequested}>
              <NotesDisplay notes={notesText} />
            </InlineEditContainer>
          }
        />
        {isExternal && (
          <ExternalInstructionsDisplay
            isLoading={mpLoading}
            name='Customer Instructions'
            value={instructionsFromCustomer}
          />
        )}
        {isExternal && isFpCarrierPortalEnabled && (
          <ExternalComplianceRequirements
            isLoading={mpLoading}
            requirements={shipperInsuranceRequirement}
            value={instructionsFromCustomer}
          />
        )}
      </Card>
    )
  }
)
